/**
 * Liabilities Panel — data table with filtering, group-by-type subtotals,
 * sorting, and right-hand slideout detail panel for individual liability entries.
 *
 * Reads canonical liability entries from the `liabilityEntries` table.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { BulkActionBar, BulkActionButton } from "@/ui/composed/bulk-action-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  Receipt,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { usePersistedSet } from "@/utils/use-persisted-set";
import { LiabilityPanel } from "./LiabilityPanel";
import { AddLiabilityPanel } from "./AddLiabilityPanel";
import { BulkValuationDialog } from "../shared-panels/BulkValuationDialog";
import type { CreditorOwedSourceUI, LiabilityEntryUI } from "./types";
import {
  useNewOutputBoundary,
  isNewOutput,
} from "../../../_shared/use-new-output-boundary";
import { NewOutputBadge } from "../../../_shared/new-output-badge";
import { type PodStatusRow } from "@/components/jobs/phases/recoveries/podShared";
import {
  PodAdjudicatedBadge,
  PodReceivedBadge,
} from "@/components/jobs/phases/recoveries/podStatusDisplay";
import {
  useLinkCounts,
  useLinkedRecordStack,
} from "../shared-panels/linkShared";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import { findContactForSource } from "@/components/jobs/phases/contacts/find-contact-for-source";
import { RelatedPartyBadge } from "@/components/jobs/phases/_shared/RelatedPartyBadge";
import type { RelatedPartyMatchUI } from "@/components/jobs/phases/_shared/relatedPartyShared";
import {
  RANKING_BADGE_COLORS,
  RANKING_GROUP_ORDER,
  RANKING_LABELS,
  RANKING_OPTIONS,
  RANKING_SORT_ORDER,
  creditorRanking,
  type CreditorRanking,
} from "./ranking";
import { LinkIndicator } from "../shared-panels/LinkIndicator";
import { SecondaryDetailPanel } from "../shared-panels/SecondaryDetailPanel";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONFIDENCE_COLORS: Record<string, { badge: BadgeColor; color: string }> =
  {
    high: { badge: "emerald", color: "text-emerald-700 dark:text-emerald-400" },
    medium: { badge: "amber", color: "text-amber-700 dark:text-amber-400" },
    low: { badge: "red", color: "text-red-700 dark:text-red-400" },
  };

const SOURCE_LABELS: Record<string, string> = {
  aged_payables: "Aged payables",
  contract: "Contract",
  director_disclosure: "Director disclosure",
  invoice: "Invoice",
  ledger: "Ledger",
  bank_statement: "Bank statement",
  proof_of_debt: "Proof of Debt",
  other: "Other",
};

const NATURE_LABELS: Record<string, string> = {
  trade: "Trade",
  lease: "Lease",
  tax: "Tax",
  utility: "Utility",
  professional_fees: "Prof. fees",
  loan: "Loan",
  insurer: "Insurer",
  other: "Other",
};

const NATURE_BADGE_COLORS: Record<string, BadgeColor> = {
  trade: "blue",
  lease: "violet",
  tax: "red",
  utility: "amber",
  professional_fees: "sky",
  loan: "emerald",
  insurer: "teal",
  other: "gray",
};

const fmt = (v: number) =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 0 })}`;

const fmtFull = (v: number) =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LiabilityGroup {
  /**
   * Display key for the group header. `null` means "do not render a group
   * header" (used for the flat-list code path when grouping is disabled).
   */
  key: string | null;
  label: string;
  /** Badge colour to use for the group-header pill. */
  badgeColor: BadgeColor;
  entries: LiabilityEntryUI[];
  total: number;
}

/** Grouping dimension for the Creditors table. `"none"` renders a flat list. */
type GroupBy = "type" | "ranking" | "none";

/** Ranking filter values — `""` means all rows. */
type RankingFilter = "" | CreditorRanking;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function LiabilitiesPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the "Total Liabilities" header and the "Add
   * Liability" button so the panel renders as table-only. Used by the
   * Overview (Plan) page.
   */
  compact?: boolean;
}) {
  const [confFilter, setConfFilter] = useState<string>("");
  const [natureFilter, setNatureFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [rankingFilter, setRankingFilter] = useState<RankingFilter>("");
  const [groupBy, setGroupBy] = useState<GroupBy>("type");
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  // Excluded creditors stay in the register but are dropped from every total,
  // the waterfall, POD and reporting. They are hidden from the main list; the
  // "Excluded" view toggle reveals them (mirrors the Assets "Excluded" tab).
  const [showExcluded, setShowExcluded] = useState(false);
  // Bulk multi-select — scoped to the current view (cleared when the Active /
  // Excluded toggle flips).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkValuationOpen, setBulkValuationOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const linkCounts = useLinkCounts(jobId);

  const bulkToggleExcluded = useMutation(
    api.jobs.summarization.agents.liabilities.publicMutations
      .bulkToggleExcluded,
  );
  const bulkAddOwedEstimate = useMutation(
    api.jobs.summarization.agents.liabilities.publicMutations
      .bulkAddOwedEstimate,
  );
  // MOR-626 — groups collapsed by default; the user's expand/collapse choice
  // persists globally to localStorage (shared with the overview view).
  const [expandedGroups, setExpandedGroups] = usePersistedSet(
    "creditors:expanded-groups",
  );

  // Stack of side-by-side linked-record panels. Lets the practitioner peel
  // through e.g. Creditor → Loan → Security Interest without losing context.
  const linkedStack = useLinkedRecordStack(
    selectedEntryId ? { type: "creditor", id: selectedEntryId } : null,
  );

  const { data: allEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId },
    ),
  );

  const newSince = useNewOutputBoundary(jobId, "sum_general_liabilities");
  const { data: jobContacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );

  const entries = useMemo(
    () => (allEntries ?? []) as LiabilityEntryUI[],
    [allEntries],
  );

  const contactIdByEntry = useMemo(() => {
    const map = new Map<string, Id<"jobContacts">>();
    for (const entry of entries) {
      const contactId = findContactForSource(
        jobContacts ?? [],
        "liability",
        entry._id,
        entry.creditorName,
      );
      if (contactId) map.set(entry._id, contactId);
    }
    return map;
  }, [entries, jobContacts]);

  const activeEntries = useMemo(
    () => entries.filter((e) => !e.isExcluded),
    [entries],
  );
  const excludedEntries = useMemo(
    () => entries.filter((e) => e.isExcluded),
    [entries],
  );
  // What the table works from: the active register by default, or the excluded
  // rows when the practitioner switches to the Excluded view.
  const baseEntries = showExcluded ? excludedEntries : activeEntries;

  // POD adjudication status per creditor row (MOR-784) — claims are lodged
  // and adjudicated in the Recoveries phase; the creditors table surfaces
  // "POD received" / "POD adjudicated" so the practitioner has portal-style
  // visibility without leaving the table.
  const { data: rawPodStatuses } = useQuery(
    convexQuery(
      api.jobs.recoveries.publicQueries.listPodStatusForLiabilityEntries,
      { jobId },
    ),
  );
  const podStatusByEntry = useMemo(() => {
    const map = new Map<string, PodStatusRow>();
    for (const row of (rawPodStatuses ?? []) as PodStatusRow[]) {
      map.set(row.liabilityEntryId, row);
    }
    return map;
  }, [rawPodStatuses]);

  // Creditors who are also on the related parties register. Derived by name
  // from the register rather than stored on the row, so a creditor lights up
  // as soon as the register identifies them.
  const { data: relatedFlags } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.relatedParties.publicQueries
        .listRelatedPartyFlagsForJob,
      { jobId },
    ),
  );
  const relatedByEntry = useMemo(() => {
    const map = new Map<string, RelatedPartyMatchUI>();
    for (const { entryId, ...match } of relatedFlags?.creditors ?? []) {
      map.set(entryId, match);
    }
    return map;
  }, [relatedFlags]);

  const filtered = useMemo(() => {
    return baseEntries.filter((e) => {
      if (confFilter && e.confidence !== confFilter) return false;
      if (natureFilter && e.natureOfDebt !== natureFilter) return false;
      if (sourceFilter && e.source !== sourceFilter) return false;
      if (rankingFilter && creditorRanking(e) !== rankingFilter) return false;
      return true;
    });
  }, [baseEntries, confFilter, natureFilter, sourceFilter, rankingFilter]);

  // -------------------------------------------------------------------------
  // Sort: statutory ranking first, then amount descending. Mirror of the
  // Assets panel, so the Creditors table floats the rows that get paid first
  // to the top of each grouping. `secured` is maintained by the
  // security-interest link sync; `preferential` is set by the practitioner.
  // -------------------------------------------------------------------------

  const sortedFiltered = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const byRank =
        RANKING_SORT_ORDER[creditorRanking(a)] -
        RANKING_SORT_ORDER[creditorRanking(b)];
      if (byRank !== 0) return byRank;
      return b.amount - a.amount;
    });
  }, [filtered]);

  // -------------------------------------------------------------------------
  // Grouping — the table can group by "Type" (default, nature of debt) or by
  // "Ranking" (Secured, then Preferred, then Unsecured). When the active
  // filter would collapse the group dimension to a single bucket (e.g.
  // natureFilter is set while groupBy="type"), we skip grouping entirely to
  // avoid showing a single redundant header row.
  // -------------------------------------------------------------------------

  const isGrouped = useMemo(() => {
    if (sortedFiltered.length === 0) return false;
    // The Excluded view is a flat list, mirroring the Assets Excluded tab.
    if (showExcluded) return false;
    if (groupBy === "none") return false;
    if (groupBy === "type") return !natureFilter;
    return !rankingFilter;
  }, [
    groupBy,
    natureFilter,
    rankingFilter,
    sortedFiltered.length,
    showExcluded,
  ]);

  const groups: LiabilityGroup[] = useMemo(() => {
    if (!isGrouped) {
      return [
        {
          key: null,
          label: "",
          badgeColor: "gray",
          entries: sortedFiltered,
          total: 0,
        },
      ];
    }

    if (groupBy === "ranking") {
      // Empty classes are dropped rather than shown as empty headers, so a
      // job with no preferred creditors reads exactly as it did before.
      return RANKING_GROUP_ORDER.flatMap((ranking) => {
        const rankEntries = sortedFiltered.filter(
          (e) => creditorRanking(e) === ranking,
        );
        if (rankEntries.length === 0) return [];
        return [
          {
            key: ranking,
            label: RANKING_LABELS[ranking],
            badgeColor: RANKING_BADGE_COLORS[ranking],
            entries: rankEntries,
            total: rankEntries.reduce(
              (s, e) => (e.isExcluded ? s : s + e.amount),
              0,
            ),
          },
        ];
      });
    }

    const map = new Map<string, LiabilityEntryUI[]>();
    for (const entry of sortedFiltered) {
      const key = entry.natureOfDebt;
      const bucket = map.get(key);
      if (bucket) {
        bucket.push(entry);
      } else {
        map.set(key, [entry]);
      }
    }

    return Array.from(map.entries())
      .map(([type, groupEntries]) => ({
        key: type,
        label: NATURE_LABELS[type] ?? type,
        badgeColor: NATURE_BADGE_COLORS[type] ?? "gray",
        entries: groupEntries,
        total: groupEntries.reduce(
          (s, e) => (e.isExcluded ? s : s + e.amount),
          0,
        ),
      }))
      .sort((a, b) => b.total - a.total);
  }, [sortedFiltered, isGrouped, groupBy]);

  // -------------------------------------------------------------------------
  // Totals
  // -------------------------------------------------------------------------

  // Excluded creditors never count towards the total (they show $0 in the
  // Excluded view, matching the Assets pattern).
  const totalAmount = useMemo(
    () => filtered.reduce((s, e) => (e.isExcluded ? s : s + e.amount), 0),
    [filtered],
  );
  const highCount = useMemo(
    () => filtered.filter((e) => e.confidence === "high").length,
    [filtered],
  );
  const singleSourceCount = useMemo(
    () => filtered.filter((e) => e.sourceDocuments.length <= 1).length,
    [filtered],
  );

  const hasFilters =
    confFilter || natureFilter || sourceFilter || rankingFilter;

  // -------------------------------------------------------------------------
  // Bulk selection (checkbox column + floating action bar)
  // -------------------------------------------------------------------------

  // Selection is scoped to the current view, so clear it when the Active /
  // Excluded toggle flips.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [showExcluded]);

  const selectionEnabled = !isViewer;
  const visibleIds = useMemo(
    () => sortedFiltered.map((e) => e._id),
    [sortedFiltered],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  // In the Excluded view the selection is already excluded → re-include;
  // otherwise → exclude.
  const bulkExcludeIntent = !showExcluded;

  const handleBulkExclude = useCallback(async () => {
    const ids = [...selectedIds] as Id<"liabilityEntries">[];
    if (ids.length === 0) return;
    try {
      const res = await bulkToggleExcluded({
        entryIds: ids,
        isExcluded: bulkExcludeIntent,
      });
      toast.success(
        `${bulkExcludeIntent ? "Excluded" : "Re-included"} ${res.updated} creditor${
          res.updated === 1 ? "" : "s"
        }`,
      );
      setSelectedIds(new Set());
    } catch {
      toast.error("Couldn't update the selected creditors. Please try again.");
    }
  }, [selectedIds, bulkExcludeIntent, bulkToggleExcluded]);

  const handleBulkValuation = useCallback(
    async (input: { amount: number; asAtDate?: string; notes?: string }) => {
      const ids = [...selectedIds] as Id<"liabilityEntries">[];
      if (ids.length === 0) return;
      setBulkSubmitting(true);
      try {
        const res = await bulkAddOwedEstimate({
          entryIds: ids,
          amount: input.amount,
          asAtDate: input.asAtDate,
          notes: input.notes,
        });
        toast.success(
          `Updated owed amount on ${res.updated} creditor${
            res.updated === 1 ? "" : "s"
          }`,
        );
        setBulkValuationOpen(false);
        setSelectedIds(new Set());
      } catch {
        toast.error("Couldn't update the owed amounts. Please try again.");
      } finally {
        setBulkSubmitting(false);
      }
    },
    [selectedIds, bulkAddOwedEstimate],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );
  const selectedContactId = selectedEntry
    ? (contactIdByEntry.get(selectedEntry._id) ?? null)
    : null;

  const totalColCount = isGrouped ? 7 : 8;

  // -------------------------------------------------------------------------
  // Collapse / expand helpers
  // -------------------------------------------------------------------------

  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const allGroupTypes = useMemo(
    () => groups.map((g) => g.key).filter((key): key is string => key !== null),
    [groups],
  );

  const allExpanded =
    allGroupTypes.length > 0 &&
    allGroupTypes.every((t) => expandedGroups.has(t));

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(allGroupTypes));
    }
  };

  // -------------------------------------------------------------------------
  // Empty / loading states
  // -------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting creditors...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <Receipt className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No creditor entries yet.
        </p>
        {!isViewer && (
          <>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 gap-1.5"
              onClick={() => setAddPanelOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Creditor
            </Button>
            <AddLiabilityPanel
              jobId={jobId}
              taskId={taskId}
              open={addPanelOpen}
              onClose={() => setAddPanelOpen(false)}
              onCreated={(id) => {
                setAddPanelOpen(false);
                setSelectedEntryId(id);
              }}
            />
          </>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header — Total Liabilities + Add button (hidden in compact mode) */}
        {!compact && (
          <div className="flex items-start justify-between px-5 pb-4 pt-5">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Total Owed
              </p>
              <p className="text-2xl font-bold tabular-nums text-red-700 dark:text-red-400">
                {fmt(totalAmount)}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {filtered.length} creditor{filtered.length !== 1 ? "s" : ""}
                {highCount > 0 && <> · {highCount} high confidence</>}
                {singleSourceCount > 0 && (
                  <> · {singleSourceCount} single source</>
                )}
              </p>
            </div>
            {!isViewer && (
              <Button
                size="sm"
                onClick={() => setAddPanelOpen(true)}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Creditor
              </Button>
            )}
          </div>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-5 border-b border-border px-5 pb-3 pt-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              View
            </label>
            <div className="flex h-8 items-center rounded-md border border-input p-0.5">
              <button
                type="button"
                onClick={() => setShowExcluded(false)}
                className={cn(
                  "h-7 rounded px-2.5 text-xs font-medium transition-colors",
                  !showExcluded
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setShowExcluded(true)}
                className={cn(
                  "h-7 rounded px-2.5 text-xs font-medium transition-colors",
                  showExcluded
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Excluded
                {excludedEntries.length > 0
                  ? ` (${excludedEntries.length})`
                  : ""}
              </button>
            </div>
          </div>
          <FilterSelect
            label="Confidence"
            value={confFilter}
            onChange={setConfFilter}
            options={[
              { value: "", label: "All" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
            ]}
          />
          <FilterSelect
            label="Type"
            value={natureFilter}
            onChange={setNatureFilter}
            options={[
              { value: "", label: "All" },
              ...Object.entries(NATURE_LABELS).map(([k, v]) => ({
                value: k,
                label: v,
              })),
            ]}
          />
          <FilterSelect
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[
              { value: "", label: "All" },
              ...Object.entries(SOURCE_LABELS).map(([k, v]) => ({
                value: k,
                label: v,
              })),
            ]}
          />
          <FilterSelect
            label="Ranking"
            value={rankingFilter}
            onChange={(v) => setRankingFilter(v as RankingFilter)}
            options={[
              { value: "", label: "All" },
              ...RANKING_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              })),
            ]}
          />
          <FilterSelect
            label="Group by"
            value={groupBy}
            onChange={(v) => setGroupBy(v as GroupBy)}
            options={[
              { value: "type", label: "Type" },
              { value: "ranking", label: "Ranking" },
              { value: "none", label: "None" },
            ]}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setConfFilter("");
                setNatureFilter("");
                setSourceFilter("");
                setRankingFilter("");
              }}
              className="pb-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Clear filters
            </button>
          )}
          {isGrouped && (
            <button
              type="button"
              onClick={toggleAll}
              className="ml-auto flex items-center gap-1 whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronsUpDown className="h-3 w-3" />
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>

        {/* Data table */}
        <div className="min-w-0">
          <div className="max-h-[calc(100vh-460px)] overflow-auto">
            <Table className="w-full table-fixed">
              {/*
                Fixed column widths so badge columns (Source / Ranking /
                Confidence) stay on one line. Type is hidden when grouped.
              */}
              <colgroup>
                <col className="w-[22%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                {!isGrouped && <col className="w-[10%]" />}
                <col className="w-[10%]" />
                <col className="w-[14%]" />
                <col />
              </colgroup>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-transparent">
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="flex items-center gap-2.5">
                      {selectionEnabled && (
                        <Checkbox
                          aria-label="Select all creditors"
                          checked={
                            allVisibleSelected
                              ? true
                              : someVisibleSelected
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={toggleSelectAll}
                        />
                      )}
                      Creditor
                    </div>
                  </TableHead>
                  <TableHead className="h-10 whitespace-nowrap text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Est Owed
                  </TableHead>
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Source
                  </TableHead>
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Ranking
                  </TableHead>
                  {!isGrouped && (
                    <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Type
                    </TableHead>
                  )}
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Confidence
                  </TableHead>
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    POD Received
                  </TableHead>
                  <TableHead className="h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    POD Adjudicated
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {sortedFiltered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={totalColCount}
                      className="h-32 text-center"
                    >
                      <span className="text-sm text-muted-foreground">
                        No creditors match the selected filters.
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => {
                    const groupKey = group.key;
                    const isExpanded =
                      !isGrouped || !groupKey || expandedGroups.has(groupKey);
                    return (
                      <Fragment key={groupKey ?? "ungrouped"}>
                        {/* Group header row */}
                        {isGrouped && groupKey && (
                          <TableRow
                            className="cursor-pointer select-none border-t border-border bg-muted/40 hover:bg-muted/50"
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <TableCell className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                )}
                                <Badge
                                  color={group.badgeColor}
                                  variant="soft"
                                  size="sm"
                                >
                                  {group.label}
                                </Badge>
                                <span className="text-xs font-medium text-muted-foreground">
                                  {group.entries.length} creditor
                                  {group.entries.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-2.5 text-right">
                              <span className="text-sm font-bold tabular-nums text-red-700 dark:text-red-400">
                                {fmtFull(group.total)}
                              </span>
                            </TableCell>
                            <TableCell colSpan={isGrouped ? 5 : 6} />
                          </TableRow>
                        )}

                        {/* Entry rows (hidden when collapsed) */}
                        {isExpanded &&
                          group.entries.map((entry) => {
                            const isSelected = selectedEntryId === entry._id;
                            const conf =
                              CONFIDENCE_COLORS[entry.confidence] ??
                              CONFIDENCE_COLORS.low;
                            const ranking = creditorRanking(entry);
                            const relatedParty = relatedByEntry.get(entry._id);

                            return (
                              <TableRow
                                key={entry._id}
                                className={cn(
                                  "cursor-pointer transition-colors",
                                  isSelected
                                    ? "hover:bg-primary/8 bg-primary/5"
                                    : "hover:bg-muted/50",
                                )}
                                onClick={() => setSelectedEntryId(entry._id)}
                              >
                                <TableCell className="px-4 py-3 align-top">
                                  <div className="flex min-w-0 items-center gap-2">
                                    {selectionEnabled && (
                                      <span
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center"
                                      >
                                        <Checkbox
                                          aria-label={`Select ${entry.creditorName}`}
                                          checked={selectedIds.has(entry._id)}
                                          onCheckedChange={() =>
                                            toggleSelect(entry._id)
                                          }
                                        />
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className={cn(
                                        "truncate text-left text-sm font-semibold underline-offset-2 hover:underline",
                                        entry.isExcluded
                                          ? "text-muted-foreground line-through"
                                          : contactIdByEntry.has(entry._id)
                                            ? "text-primary"
                                            : "text-foreground",
                                      )}
                                      title={
                                        contactIdByEntry.has(entry._id)
                                          ? `Open ${entry.creditorName}'s contact card`
                                          : entry.creditorName
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        const contactId = contactIdByEntry.get(
                                          entry._id,
                                        );
                                        if (contactId) {
                                          setViewingContactId(contactId);
                                          return;
                                        }
                                        setSelectedEntryId(entry._id);
                                      }}
                                    >
                                      {entry.creditorName}
                                    </button>
                                    {isNewOutput(
                                      entry._creationTime,
                                      newSince,
                                    ) && <NewOutputBadge />}

                                    {entry.isExcluded && (
                                      <Badge
                                        color="gray"
                                        variant="soft"
                                        size="sm"
                                      >
                                        Excluded
                                      </Badge>
                                    )}
                                    <LinkIndicator
                                      count={
                                        linkCounts.byLinkedEntity.get(
                                          entry._id,
                                        ) ?? 0
                                      }
                                    />
                                  </div>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-right align-top">
                                  <span
                                    className={cn(
                                      "font-mono text-sm font-semibold tabular-nums",
                                      entry.isExcluded
                                        ? "text-muted-foreground line-through"
                                        : "text-red-700 dark:text-red-400",
                                    )}
                                  >
                                    {fmt(entry.amount)}
                                  </span>
                                  <AmountProvenance entry={entry} />
                                </TableCell>
                                <TableCell className="px-4 py-3 align-top">
                                  <Badge color="blue" variant="soft" size="sm">
                                    {SOURCE_LABELS[entry.source] ??
                                      entry.source}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 align-top">
                                  <div className="flex flex-wrap items-center gap-1">
                                    <Badge
                                      color={RANKING_BADGE_COLORS[ranking]}
                                      variant="soft"
                                      size="sm"
                                    >
                                      {RANKING_LABELS[ranking]}
                                    </Badge>
                                    {relatedParty && (
                                      <RelatedPartyBadge match={relatedParty} />
                                    )}
                                  </div>
                                </TableCell>
                                {!isGrouped && (
                                  <TableCell className="px-4 py-3 align-top">
                                    <Badge
                                      color="gray"
                                      variant="soft"
                                      size="sm"
                                    >
                                      {NATURE_LABELS[entry.natureOfDebt] ??
                                        entry.natureOfDebt}
                                    </Badge>
                                  </TableCell>
                                )}
                                <TableCell className="px-4 py-3 align-top">
                                  <Badge
                                    color={conf.badge}
                                    variant="soft"
                                    size="sm"
                                  >
                                    {entry.confidence.charAt(0).toUpperCase() +
                                      entry.confidence.slice(1)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 align-top">
                                  <PodReceivedBadge
                                    status={podStatusByEntry.get(entry._id)}
                                  />
                                </TableCell>
                                <TableCell className="px-4 py-3 align-top">
                                  <PodAdjudicatedBadge
                                    status={podStatusByEntry.get(entry._id)}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </Fragment>
                    );
                  })
                )}

                {/* Grand total row */}
                {sortedFiltered.length > 0 && (
                  <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                    <TableCell className="px-4 py-3 text-sm font-bold text-muted-foreground">
                      Total ({filtered.length} creditor
                      {filtered.length !== 1 ? "s" : ""})
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <span className="font-mono text-sm font-bold tabular-nums text-red-700 dark:text-red-400">
                        {fmt(totalAmount)}
                      </span>
                    </TableCell>
                    <TableCell colSpan={isGrouped ? 5 : 6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Linked names open the contact card · Click the row (or an unlinked
            name) for creditor details
          </p>
        </div>
      </div>

      {/* Liability detail slideout */}
      <LiabilityPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        onDeleted={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        onMerged={(targetId) => {
          // Jump to the surviving creditor so the practitioner sees the result.
          setSelectedEntryId(targetId);
          linkedStack.closeAll();
        }}
        isViewer={isViewer}
        onOpenLinked={linkedStack.open}
        onViewContact={
          selectedContactId
            ? () => setViewingContactId(selectedContactId)
            : undefined
        }
      />

      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={jobContacts ?? []}
      />

      {/* Stack of side-by-side linked records */}
      <SecondaryDetailPanel
        jobId={jobId}
        stack={linkedStack.stack}
        onCloseAt={linkedStack.closeAt}
        onOpenLinked={linkedStack.open}
        isViewer={isViewer}
      />

      {/* Add liability form */}
      <AddLiabilityPanel
        jobId={jobId}
        taskId={taskId}
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        onCreated={(id) => {
          setAddPanelOpen(false);
          setSelectedEntryId(id);
        }}
      />

      {/* Bulk action bar (checkbox selection) */}
      {selectionEnabled && (
        <BulkActionBar
          count={selectedIds.size}
          onDone={() => setSelectedIds(new Set())}
        >
          <BulkActionButton onClick={() => setBulkValuationOpen(true)}>
            Update owed amounts
          </BulkActionButton>
          <BulkActionButton
            onClick={handleBulkExclude}
            tone={bulkExcludeIntent ? "danger" : "default"}
          >
            {bulkExcludeIntent ? "Exclude" : "Re-include"}
          </BulkActionButton>
        </BulkActionBar>
      )}

      <BulkValuationDialog
        open={bulkValuationOpen}
        onOpenChange={setBulkValuationOpen}
        count={selectedIds.size}
        title="Update owed amounts"
        itemNoun="creditor"
        amountLabel="Amount owed ($)"
        submitting={bulkSubmitting}
        onSubmit={handleBulkValuation}
      />
    </div>
  );
}

/**
 * Where the row's figure came from. A creditor can be carrying the company's
 * own records, the amount the creditor claimed, or the amount admitted on
 * adjudication, and which one it is changes how much weight it deserves — so
 * the table says, rather than leaving every figure looking equally solid.
 */
const AMOUNT_SOURCE_CAPTION: Record<CreditorOwedSourceUI, string> = {
  document: "from company records",
  proof_of_debt: "claimed on POD",
  pod_admitted: "admitted on adjudication",
  manual: "practitioner estimate",
};

function AmountProvenance({ entry }: { entry: LiabilityEntryUI }) {
  if (!entry.amountSource) return null;
  return (
    <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
      {AMOUNT_SOURCE_CAPTION[entry.amountSource]}
      {entry.selectedOwedEstimate ? "" : " \u00B7 unconfirmed"}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Filter select
// ---------------------------------------------------------------------------

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <Select
        value={value || "all"}
        onValueChange={(v) => onChange(v === "all" ? "" : v)}
      >
        <SelectTrigger className="h-8 min-w-[110px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value || "all"} value={o.value || "all"}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
